import React, {useState} from 'react';
import {toast} from 'react-toastify';
import {useTranslation} from 'react-i18next';
import act02 from '../../../assets/img/icons/actions/gridicons_cross-circle.svg';
import act03 from '../../../assets/img/icons/actions/el_pencil-alt.svg';
import act04 from '../../../assets/img/icons/actions/fluent_print-28-filled.svg';
import check from '../../../assets/img/icons/check.svg';
import infoButton from '../../../assets/img/icons/actions/info.svg';
import PopupStatus from '../../MyOrders/MyOrdersPopups/PopupStatus';
import ReceiveConfirmationPopup from './ReceiveConfirmationPopup';
import {OrdersType} from '../../../types/TypesStore';
import {
	useDeleteOrderMutation,
	useLazyGetOrderViewQuery,
} from '../../../redux/DepotSlice/depotOrdersApi';
import {useNavigate} from 'react-router-dom';
import TableButtonsActions, {
	ActionConfig,
} from '../../../Components/TableButtonsActions/TableButtonsActions';

type Props = {
	order: OrdersType;
};

const InvoiceActions: React.FC<Props> = ({order}) => {
	const {t} = useTranslation();
	const navigate = useNavigate();

	const [openInfoPopup, setOpenInfoPopup] = useState<boolean>(false);
	const [openReceivePopup, setOpenReceivePopup] = useState<boolean>(false);
	const [trigger, {data, isLoading}] = useLazyGetOrderViewQuery();
	const [deleteOrder] = useDeleteOrderMutation();

	// === HANDLERS ===
	const handlers = {
		delete: async (id: number) => {
			try {
				await deleteOrder(id);
				toast.success(t('Успішно видалено'));
			} catch (error: any) {
				// Получаем сообщение об ошибке из API ответа
				const errorMessage =
					error?.data?.message?.textmsg ||
					error?.message ||
					t('Помилка при видаленні замовлення');
				toast.error(errorMessage);
			}
		},
		edit: async (id: number) => {
			navigate(`/create_invoice/${id}`);
		},
		print: (id: number) => {
			navigate(`/print_invoice/${id}/pdf`);
		},
		approve: () => {
			setOpenReceivePopup(true);
		},
		info: (id: number) => {
			trigger(id);
			setOpenInfoPopup(true);
		},
	};

	const actions: ActionConfig[] = [
		{
			type: 'delete',
			icon: act02,
			label: t('Видалити'),
			requiresConfirmation: true,
			confirmText: t('Ви дійсно хочете видалити замовлення?'),
			disabled: (status) => !(status === 'created' || status === 'confirmed'),
			handler: handlers.delete,
		},
		{
			type: 'edit',
			icon: act03,
			label: t('Редагувати'),
			disabled: (status) => !(status === 'created' || status === 'confirmed'),
			handler: handlers.edit,
		},
		{
			type: 'print',
			icon: act04,
			label: t('Друк'),
			disabled: (status) => status === 'created' || status === 'rejected',
			handler: handlers.print,
		},
		{
			type: 'approve',
			icon: check,
			label: t('Підтвердження отримання'),
			disabled: (status, type) => status !== 'shipped' || type !== 'income',
			handler: handlers.approve,
		},
		{
			type: 'info',
			icon: infoButton,
			label: t('Інфо'),
			handler: handlers.info,
		},
	];

	const handleClosePopup = () => {
		setOpenInfoPopup(false);
	};

	const handleCloseReceivePopup = () => {
		setOpenReceivePopup(false);
	};

	const handleReceiveSuccess = () => {
		// После успешного подтверждения получения закрываем попап
		setOpenReceivePopup(false);
	};

	return (
		<>
			{openInfoPopup && !isLoading && (
				<PopupStatus
					closePopup={handleClosePopup}
					editButton={handlers.edit}
					removeButton={handlers.delete}
					order={data.order}
					orderHistory={data.orderHistory}
				/>
			)}
			{openReceivePopup && (
				<ReceiveConfirmationPopup
					orderId={order.id}
					onClose={handleCloseReceivePopup}
					onSuccess={handleReceiveSuccess}
				/>
			)}
			<TableButtonsActions
				actions={actions}
				orderId={order.id}
				orderStatus={order.status}
				orderType={order.type}
			/>
		</>
	);
};

export default InvoiceActions;
